import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const DEFAULT_POLICY = {
  smoothing: 0.72,
  minDistance: 1.4,
  strokeWidth: 2.4,
  pressure: false,
  rawEvents: true,
  coalesced: true,
  palmContactThreshold: 54,
  palmHardRejectContact: 92,
  palmPressureCeiling: 0.08,
  palmSecondaryContactRatio: 0.72,
  maxGapPx: 26,
  color: "#1558ff",
  rawColor: "rgba(15, 23, 42, 0.22)"
};

const DEVICE_POLICIES = {
  "windows-touch": { smoothing: 0.9, minDistance: 1.8, strokeWidth: 2.6, palmContactThreshold: 66, palmHardRejectContact: 108 },
  "touch-laptop": { smoothing: 0.95, minDistance: 2, strokeWidth: 2.7, palmContactThreshold: 68, palmHardRejectContact: 112 },
  "android-tablet": { smoothing: 0.68, minDistance: 1.2, strokeWidth: 2.35, palmContactThreshold: 62, palmHardRejectContact: 102 },
  "ios-tablet": { smoothing: 0.52, minDistance: 1.05, strokeWidth: 2.2, palmContactThreshold: 54, palmHardRejectContact: 96 },
  "mobile-touch": { smoothing: 0.8, minDistance: 1.7, strokeWidth: 2.8, palmContactThreshold: 56, palmHardRejectContact: 96 },
  desktop: { smoothing: 0.6, minDistance: 1, strokeWidth: 2.2, palmContactThreshold: 52, palmHardRejectContact: 92 }
};

const INPUT_POLICIES = {
  "active-pen": { smoothing: 0.38, minDistance: 0.75, strokeWidth: 2.2, pressure: true, palmContactThreshold: 46, palmHardRejectContact: 86 },
  "apple-pencil": { smoothing: 0.32, minDistance: 0.65, strokeWidth: 2.15, pressure: true, palmContactThreshold: 44, palmHardRejectContact: 84 },
  "capacitive-disc": { smoothing: 0.82, minDistance: 1.25, strokeWidth: 2.45, pressure: false, palmContactThreshold: 64, palmHardRejectContact: 104 },
  finger: { smoothing: 1.08, minDistance: 2.4, strokeWidth: 4.4, pressure: false, palmContactThreshold: 78, palmHardRejectContact: 128 },
  mouse: { smoothing: 0.48, minDistance: 0.85, strokeWidth: 2.05, pressure: false },
  "unknown-touch": { smoothing: 0.9, minDistance: 1.8, strokeWidth: 2.8, pressure: false, palmContactThreshold: 68, palmHardRejectContact: 112 }
};

const COMBO_POLICIES = {
  "windows-touch:active-pen": { smoothing: 0.42, minDistance: 0.7, strokeWidth: 2.1, pressure: true, palmContactThreshold: 70, palmHardRejectContact: 116 },
  "windows-touch:capacitive-disc": { smoothing: 0.95, minDistance: 1.55, strokeWidth: 2.65, palmContactThreshold: 72, palmHardRejectContact: 118 },
  "touch-laptop:active-pen": { smoothing: 0.5, minDistance: 0.95, strokeWidth: 2.35, pressure: true, palmContactThreshold: 72, palmHardRejectContact: 120 },
  "android-tablet:capacitive-disc": { smoothing: 0.78, minDistance: 1.15, strokeWidth: 2.35 },
  "ios-tablet:apple-pencil": { smoothing: 0.28, minDistance: 0.6, strokeWidth: 2.05, pressure: true }
};

const EMPTY_INPUT_PROFILE = {
  inputKind: "waiting",
  pointerType: "none",
  confidence: "none",
  sampleCount: 0,
  sampleRateHz: 0,
  pressureSupported: false,
  averagePressure: 0,
  averageContactMajor: 0,
  maxContactMajor: 0,
  coalescedSupported: false,
  rawPointerSupported: false
};

function round(value, digits = 2) {
  if (!Number.isFinite(value)) return 0;
  const multiplier = 10 ** digits;
  return Math.round(value * multiplier) / multiplier;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function mean(values) {
  const filtered = values.filter((value) => Number.isFinite(value));
  if (!filtered.length) return 0;
  return filtered.reduce((sum, value) => sum + value, 0) / filtered.length;
}

function distance(a, b) {
  if (!a || !b) return 0;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function detectBrowser(userAgent) {
  const ua = userAgent.toLowerCase();
  if (ua.includes("edg/")) return "edge";
  if (ua.includes("firefox/")) return "firefox";
  if (ua.includes("chrome/") || ua.includes("crios/")) return "chrome";
  if (ua.includes("safari/")) return "safari";
  return "unknown";
}

function detectOs(userAgent, platform, maxTouchPoints) {
  const ua = userAgent.toLowerCase();
  const normalizedPlatform = String(platform || "").toLowerCase();
  if (/ipad|iphone|ipod/.test(ua) || (normalizedPlatform === "macintel" && maxTouchPoints > 1)) return "ios";
  if (ua.includes("android")) return "android";
  if (ua.includes("windows")) return "windows";
  if (ua.includes("mac os") || normalizedPlatform.includes("mac")) return "mac";
  if (ua.includes("linux")) return "linux";
  return "unknown";
}

export function detectDeviceProfile() {
  const nav = window.navigator || {};
  const userAgent = nav.userAgent || "";
  const platform = nav.platform || "";
  const maxTouchPoints = nav.maxTouchPoints || 0;
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches || false;
  const finePointer = window.matchMedia?.("(pointer: fine)")?.matches || false;
  const anyCoarsePointer = window.matchMedia?.("(any-pointer: coarse)")?.matches || false;
  const anyFinePointer = window.matchMedia?.("(any-pointer: fine)")?.matches || false;
  const width = window.innerWidth || 0;
  const height = window.innerHeight || 0;
  const os = detectOs(userAgent, platform, maxTouchPoints);
  const hasTouch = maxTouchPoints > 0 || anyCoarsePointer || coarsePointer;
  const shortestSide = Math.min(width, height);
  const looksTablet = hasTouch && shortestSide >= 600;
  const looksMobile = hasTouch && shortestSide < 600;
  const looksHybrid = hasTouch && anyFinePointer && os === "windows";
  let formFactor = "desktop";
  if (looksMobile) formFactor = "mobile";
  else if (looksHybrid) formFactor = "touch_laptop";
  else if (looksTablet) formFactor = "tablet";
  else if (hasTouch) formFactor = "touch_screen";
  let profileKey = "desktop";
  if (os === "windows" && hasTouch) profileKey = looksHybrid ? "touch-laptop" : "windows-touch";
  else if (os === "android" && looksTablet) profileKey = "android-tablet";
  else if (os === "ios" && looksTablet) profileKey = "ios-tablet";
  else if (looksMobile) profileKey = "mobile-touch";
  return {
    profileKey,
    formFactor,
    os,
    browser: detectBrowser(userAgent),
    hasTouch,
    maxTouchPoints,
    coarsePointer,
    finePointer,
    anyCoarsePointer,
    anyFinePointer,
    devicePixelRatio: window.devicePixelRatio || 1,
    viewport: { width, height },
    userAgent
  };
}
function getCanvasPoint(event, canvas) {
  const rect = canvas.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

function normalizePointerEvent(event, canvas, source = "pointer") {
  const point = getCanvasPoint(event, canvas);
  const width = Number.isFinite(event.width) ? event.width : 0;
  const height = Number.isFinite(event.height) ? event.height : 0;
  return {
    source,
    eventType: event.type,
    pointerId: event.pointerId ?? 1,
    pointerType: event.pointerType || "mouse",
    x: point.x,
    y: point.y,
    pressure: Number.isFinite(event.pressure) ? event.pressure : 0,
    width,
    height,
    contactMajor: Math.max(width, height) || 0,
    tiltX: Number.isFinite(event.tiltX) ? event.tiltX : null,
    tiltY: Number.isFinite(event.tiltY) ? event.tiltY : null,
    buttons: event.buttons ?? 0,
    isPrimary: event.isPrimary !== false,
    time: event.timeStamp || performance.now(),
    coalescedCount: typeof event.getCoalescedEvents === "function" ? event.getCoalescedEvents().length : 0,
    touchCount: 1
  };
}

function normalizeTouch(touch, event, canvas) {
  const rect = canvas.getBoundingClientRect();
  const radiusX = Number.isFinite(touch.radiusX) ? touch.radiusX : 0;
  const radiusY = Number.isFinite(touch.radiusY) ? touch.radiusY : 0;
  return {
    source: "touch",
    eventType: event.type,
    pointerId: touch.identifier,
    pointerType: "touch",
    x: touch.clientX - rect.left,
    y: touch.clientY - rect.top,
    pressure: Number.isFinite(touch.force) ? touch.force : 0,
    width: radiusX * 2,
    height: radiusY * 2,
    contactMajor: Math.max(radiusX, radiusY) * 2,
    tiltX: null,
    tiltY: null,
    buttons: 1,
    isPrimary: true,
    time: event.timeStamp || performance.now(),
    coalescedCount: 0,
    touchCount: event.touches?.length || event.changedTouches?.length || 1
  };
}

function collectPointerSamples(event, canvas, policy) {
  if (policy.coalesced && typeof event.getCoalescedEvents === "function") {
    const coalesced = event.getCoalescedEvents();
    if (coalesced.length > 1) {
      return coalesced.map((coalescedEvent) => normalizePointerEvent(coalescedEvent, canvas, "coalesced"));
    }
  }
  return [normalizePointerEvent(event, canvas, event.type === "pointerrawupdate" ? "raw" : "pointer")];
}

export function classifyInputProfile(samples, deviceProfile) {
  if (!samples.length) return EMPTY_INPUT_PROFILE;
  const recent = samples.slice(-120);
  const pointerCounts = recent.reduce((counts, sample) => {
    counts[sample.pointerType] = (counts[sample.pointerType] || 0) + 1;
    return counts;
  }, {});
  const pointerType = Object.entries(pointerCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "unknown";
  const contacts = recent.map((sample) => sample.contactMajor).filter((value) => value > 0);
  const pressures = recent.map((sample) => sample.pressure).filter((value) => value > 0);
  const averageContactMajor = mean(contacts);
  const maxContactMajor = contacts.length ? Math.max(...contacts) : 0;
  const averagePressure = mean(pressures);
  const pressureSupported = pointerType === "pen" || pressures.some((value) => value > 0 && value < 0.99);
  const intervals = [];
  for (let index = 1; index < recent.length; index += 1) {
    const gap = recent[index].time - recent[index - 1].time;
    if (gap > 0 && gap < 200) intervals.push(gap);
  }
  const sampleRateHz = mean(intervals) ? 1000 / mean(intervals) : 0;
  const coalescedSupported = recent.some((sample) => sample.coalescedCount > 0 || sample.source === "coalesced");
  const rawPointerSupported = recent.some((sample) => sample.source === "raw");
  let inputKind = "unknown-touch";
  let confidence = "tentative";
  if (pointerType === "pen") {
    inputKind = deviceProfile.os === "ios" ? "apple-pencil" : "active-pen";
    confidence = "high";
  } else if (pointerType === "mouse") {
    inputKind = "mouse";
    confidence = "high";
  } else if (pointerType === "touch") {
    if (averageContactMajor > 0 && averageContactMajor <= 42 && sampleRateHz >= 18) {
      inputKind = "capacitive-disc";
      confidence = "medium";
    } else if (averageContactMajor > 42) {
      inputKind = "finger";
      confidence = "medium";
    } else {
      inputKind = "unknown-touch";
      confidence = "low";
    }
  }
  return {
    inputKind,
    pointerType,
    confidence,
    sampleCount: recent.length,
    sampleRateHz: round(sampleRateHz, 1),
    pressureSupported,
    averagePressure: round(averagePressure, 3),
    averageContactMajor: round(averageContactMajor, 1),
    maxContactMajor: round(maxContactMajor, 1),
    coalescedSupported,
    rawPointerSupported
  };
}

export function selectStrokePolicy(deviceProfile, inputProfile) {
  return {
    ...DEFAULT_POLICY,
    ...(DEVICE_POLICIES[deviceProfile.profileKey] || {}),
    ...(INPUT_POLICIES[inputProfile.inputKind] || {}),
    ...(COMBO_POLICIES[`${deviceProfile.profileKey}:${inputProfile.inputKind}`] || {})
  };
}

function compactPoints(points, minDistance) {
  if (points.length < 2) return points;
  const compacted = [points[0]];
  for (const point of points.slice(1)) {
    if (distance(point, compacted[compacted.length - 1]) >= minDistance) compacted.push(point);
  }
  return compacted;
}

export function smoothStroke(points, policy) {
  const compacted = compactPoints(points, policy.minDistance);
  if (compacted.length < 3) return compacted;
  const radius = clamp(Math.round(policy.smoothing * 2), 1, 4);
  return compacted.map((point, index) => {
    if (index === 0 || index === compacted.length - 1) return point;
    const start = Math.max(0, index - radius);
    const end = Math.min(compacted.length - 1, index + radius);
    const windowPoints = compacted.slice(start, end + 1);
    const centerWeight = 1.8;
    const weightTotal = windowPoints.length + centerWeight - 1;
    const x = (windowPoints.reduce((sum, item) => sum + item.x, 0) + point.x * (centerWeight - 1)) / weightTotal;
    const y = (windowPoints.reduce((sum, item) => sum + item.y, 0) + point.y * (centerWeight - 1)) / weightTotal;
    return { ...point, x, y };
  });
}
function drawStroke(ctx, points, policy, options = {}) {
  if (points.length < 2) return;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = options.raw ? policy.rawColor : policy.color;
  ctx.globalAlpha = options.raw ? 0.85 : 1;
  if (!policy.pressure || options.raw) {
    ctx.lineWidth = options.raw ? 1 : policy.strokeWidth;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const point = points[index];
      ctx.quadraticCurveTo(previous.x, previous.y, (previous.x + point.x) / 2, (previous.y + point.y) / 2);
    }
    ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
    ctx.stroke();
  } else {
    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const point = points[index];
      const pressure = clamp(point.pressure || 0.45, 0.18, 1);
      ctx.lineWidth = policy.strokeWidth * (0.72 + pressure * 0.72);
      ctx.beginPath();
      ctx.moveTo(previous.x, previous.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
  }
  ctx.restore();
}

export function diagnoseStrokeQuality(strokes, samples, policy) {
  const allPoints = strokes.flat();
  if (!allPoints.length) {
    return {
      strokeCount: 0,
      pointCount: 0,
      averagePointGap: 0,
      largestPointGap: 0,
      discontinuities: 0,
      acceptedSamples: samples.length,
      verdict: "Waiting for ink"
    };
  }
  const gaps = [];
  strokes.forEach((stroke) => {
    for (let index = 1; index < stroke.length; index += 1) gaps.push(distance(stroke[index], stroke[index - 1]));
  });
  const largestPointGap = gaps.length ? Math.max(...gaps) : 0;
  const averagePointGap = mean(gaps);
  const discontinuities = gaps.filter((gap) => gap > policy.maxGapPx).length;
  let verdict = "Stable";
  if (discontinuities > 3) verdict = "Broken strokes detected";
  else if (averagePointGap > 8) verdict = "Sparse samples";
  else if (largestPointGap > policy.maxGapPx) verdict = "Occasional gaps";
  return {
    strokeCount: strokes.length,
    pointCount: allPoints.length,
    averagePointGap: round(averagePointGap, 1),
    largestPointGap: round(largestPointGap, 1),
    discontinuities,
    acceptedSamples: samples.length,
    verdict
  };
}


function countBy(items, keyFn) {
  return items.reduce((counts, item) => {
    const key = keyFn(item) || "unknown";
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
}

function summarizeIntervals(samples) {
  const intervals = [];
  for (let index = 1; index < samples.length; index += 1) {
    const gap = samples[index].time - samples[index - 1].time;
    if (gap > 0 && gap < 300) intervals.push(gap);
  }
  const avg = mean(intervals);
  return {
    count: intervals.length,
    avgMs: round(avg, 1),
    minMs: intervals.length ? round(Math.min(...intervals), 1) : 0,
    maxMs: intervals.length ? round(Math.max(...intervals), 1) : 0,
    inferredHz: avg ? round(1000 / avg, 1) : 0
  };
}

function summarizeSamples(acceptedSamples, rejectedSamples) {
  const allSamples = [...acceptedSamples, ...rejectedSamples];
  const pressures = acceptedSamples.map((sample) => sample.pressure).filter((value) => value > 0);
  const contacts = acceptedSamples.map((sample) => sample.contactMajor).filter((value) => value > 0);
  return {
    acceptedCount: acceptedSamples.length,
    rejectedCount: rejectedSamples.length,
    pointerTypes: countBy(allSamples, (sample) => sample.pointerType),
    sources: countBy(allSamples, (sample) => sample.source),
    eventTypes: countBy(allSamples, (sample) => sample.eventType),
    rejectionReasons: countBy(rejectedSamples, (sample) => sample.rejectionReason),
    pressure: {
      nonZeroCount: pressures.length,
      avg: round(mean(pressures), 3),
      max: pressures.length ? round(Math.max(...pressures), 3) : 0
    },
    contactMajor: {
      avg: round(mean(contacts), 1),
      max: contacts.length ? round(Math.max(...contacts), 1) : 0
    },
    timing: summarizeIntervals(acceptedSamples),
    rawEventCount: acceptedSamples.filter((sample) => sample.source === "raw").length,
    coalescedEventCount: acceptedSamples.filter((sample) => sample.source === "coalesced").length
  };
}

function summarizeStroke(stroke, index) {
  const xs = stroke.map((point) => point.x);
  const ys = stroke.map((point) => point.y);
  const gaps = [];
  for (let pointIndex = 1; pointIndex < stroke.length; pointIndex += 1) {
    gaps.push(distance(stroke[pointIndex], stroke[pointIndex - 1]));
  }
  return {
    stroke: index + 1,
    points: stroke.length,
    durationMs: round((stroke[stroke.length - 1]?.time || 0) - (stroke[0]?.time || 0), 1),
    bounds: {
      x: xs.length ? round(Math.min(...xs), 1) : 0,
      y: ys.length ? round(Math.min(...ys), 1) : 0,
      width: xs.length ? round(Math.max(...xs) - Math.min(...xs), 1) : 0,
      height: ys.length ? round(Math.max(...ys) - Math.min(...ys), 1) : 0
    },
    avgGap: round(mean(gaps), 1),
    maxGap: gaps.length ? round(Math.max(...gaps), 1) : 0
  };
}


function summarizeStrokes(strokes) {
  const summaries = strokes.map(summarizeStroke);
  const pointCounts = summaries.map((stroke) => stroke.points);
  const durations = summaries.map((stroke) => stroke.durationMs).filter((value) => value > 0);
  const maxGaps = summaries.map((stroke) => stroke.maxGap);
  const avgGaps = summaries.map((stroke) => stroke.avgGap);
  const worstByGap = [...summaries]
    .sort((a, b) => b.maxGap - a.maxGap)
    .slice(0, 8);
  return {
    count: summaries.length,
    totalPoints: pointCounts.reduce((sum, value) => sum + value, 0),
    avgPointsPerStroke: round(mean(pointCounts), 1),
    avgDurationMs: round(mean(durations), 1),
    avgGap: round(mean(avgGaps), 1),
    maxGap: maxGaps.length ? round(Math.max(...maxGaps), 1) : 0,
    worstByGap
  };
}

function percentile(values, percentileValue) {
  const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const position = (sorted.length - 1) * percentileValue;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}

function summarizeContactDistribution(samples) {
  const contacts = samples.map((sample) => Number(sample.contactMajor || 0)).filter((value) => value > 0);
  return {
    count: contacts.length,
    min: contacts.length ? round(Math.min(...contacts), 1) : 0,
    p05: round(percentile(contacts, 0.05), 1),
    p25: round(percentile(contacts, 0.25), 1),
    p50: round(percentile(contacts, 0.5), 1),
    p75: round(percentile(contacts, 0.75), 1),
    p90: round(percentile(contacts, 0.9), 1),
    p95: round(percentile(contacts, 0.95), 1),
    max: contacts.length ? round(Math.max(...contacts), 1) : 0,
    avg: round(mean(contacts), 1)
  };
}

function assessPalmCalibration(captures, deviceProfile, inputProfile) {
  const writing = captures?.writingOnly;
  const palmOnly = captures?.palmOnly;
  if (!writing || !palmOnly) {
    return {
      status: "incomplete",
      recommendedPolicy: "needs-writing-and-palm-captures",
      message: "Capture stylus-only and palm-only samples on the same device/input before trusting a palm rule."
    };
  }

  const writingP95 = Number(writing.contactDistribution?.p95 || 0);
  const writingP50 = Number(writing.contactDistribution?.p50 || 0);
  const palmDistribution = palmOnly?.contactDistribution || palmOnly?.allContactDistribution || {};
  const palmOnlyFloor = Number(palmDistribution.p25 || palmDistribution.p50 || palmDistribution.p05 || 0);
  const palmContactFloor = palmOnlyFloor;
  const palmContactP95 = Number(palmDistribution.p95 || palmDistribution.max || 0);
  const separationPx = round(palmContactFloor - writingP95, 1);
  const separationScore = writingP95 > 0 ? round(separationPx / writingP95, 3) : 0;
  const palmSampleSummary = palmOnly?.sampleSummary || {};
  const totalPalmSamples = (palmSampleSummary.acceptedCount || 0) + (palmSampleSummary.rejectedCount || 0);
  const rejectionRate = totalPalmSamples ? round((palmSampleSummary.rejectedCount || 0) / totalPalmSamples, 3) : 0;
  const isTruePen = inputProfile?.pointerType === "pen" || writing.inputProfile?.pointerType === "pen";
  const hasContactData = writingP95 > 0 && palmContactFloor > 0;
  const androidTablet = deviceProfile?.profileKey === "android-tablet";
  const hasStrongSeparation = hasContactData && ((separationPx >= 12 && separationScore >= 0.35) || (androidTablet && separationPx >= 5 && separationScore >= 0.9));
  const hasWeakSeparation = hasContactData && ((separationPx >= 6 && separationScore >= 0.18) || (androidTablet && separationPx >= 3 && separationScore >= 0.55));
  let recommendedPolicy = "no-palm-supported";
  let status = "blocked";
  let message = "Stylus and palm contacts overlap too much. Do not enable palm-rest writing for this device/input yet.";

  if (isTruePen) {
    recommendedPolicy = "native-safe";
    status = "ready";
    message = "Browser reports a true pen pointer. Prefer pen-first rejection plus secondary-touch rejection.";
  } else if (!hasContactData) {
    recommendedPolicy = "insufficient-contact-data";
    status = "incomplete";
    message = "This browser did not expose enough contact geometry to calibrate palm rejection.";
  } else if (hasStrongSeparation) {
    recommendedPolicy = androidTablet ? "android-calibrated-palm" : "calibrated-palm";
    status = "ready";
    message = "Palm contact is separable from writing contact. Test this threshold in the lab before moving it to Practice.";
  } else if (hasWeakSeparation) {
    recommendedPolicy = androidTablet ? "android-cautious-palm" : "cautious-calibrated-palm";
    status = "caution";
    message = androidTablet
      ? "Android shows weak separation. This may work only for some postures/styluses, so test carefully."
      : "There is some separation, but thresholding may reject some intended strokes.";
  }

  const rejectContactAbove = hasContactData
    ? round(Math.max(writingP95 * 1.28, palmContactFloor * 0.84), 1)
    : 0;
  const hardRejectContact = hasContactData
    ? round(Math.max(rejectContactAbove * 1.2, palmContactP95 || rejectContactAbove), 1)
    : 0;

  return {
    status,
    recommendedPolicy,
    message,
    separationScore,
    separationPx,
    rejectionRate,
    writingContactP50: round(writingP50, 1),
    writingContactP95: round(writingP95, 1),
    palmContactFloor: round(palmContactFloor, 1),
    palmContactP95: round(palmContactP95, 1),
    generatedPolicy: {
      mode: recommendedPolicy,
      palmContactThreshold: rejectContactAbove,
      palmHardRejectContact: hardRejectContact,
      rejectContactAbove,
      hardRejectContact,
      secondaryTouchReject: true,
      penFirstRejectTouch: true,
      confidence: status
    }
  };
}

function formatValue(value) {
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "object" && value !== null) return JSON.stringify(value);
  return String(value ?? "-");
}

function DataCard({ title, eyebrow, items }) {
  return (
    <section className="slate-lab-card">
      <div className="slate-lab-card-eyebrow">{eyebrow}</div>
      <h2>{title}</h2>
      <dl className="slate-lab-kv-list">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{formatValue(value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CalibrationCaptureCard({ title, capture }) {
  const captured = Boolean(capture);
  return (
    <article className={"slate-lab-calibration-mini-card " + (captured ? "is-captured" : "")}>
      <div className="slate-lab-card-eyebrow">{captured ? "captured" : "waiting"}</div>
      <h3>{title}</h3>
      {captured ? (
        <dl className="slate-lab-kv-list">
          <div><dt>samples</dt><dd>{capture.sampleSummary.acceptedCount}</dd></div>
          <div><dt>rejected</dt><dd>{capture.sampleSummary.rejectedCount}</dd></div>
          <div><dt>contact p50/p95</dt><dd>{capture.contactDistribution.p50} / {capture.contactDistribution.p95}</dd></div>
          <div><dt>all contact p95</dt><dd>{capture.allContactDistribution.p95}</dd></div>
          <div><dt>quality</dt><dd>{capture.quality.verdict}</dd></div>
        </dl>
      ) : (
        <p>Write a short sample on the canvas, then capture this step.</p>
      )}
    </article>
  );
}

function CalibrationRecommendationCard({ result }) {
  return (
    <article className={"slate-lab-calibration-result is-" + (result.status || "incomplete")}>
      <div className="slate-lab-card-eyebrow">recommendation</div>
      <h3>{result.recommendedPolicy}</h3>
      <p>{result.message}</p>
      <dl className="slate-lab-kv-list">
        <div><dt>separation</dt><dd>{result.separationPx ?? "-"} px</dd></div>
        <div><dt>score</dt><dd>{result.separationScore ?? "-"}</dd></div>
        <div><dt>write p95</dt><dd>{result.writingContactP95 ?? "-"}</dd></div>
        <div><dt>reject above</dt><dd>{result.generatedPolicy?.rejectContactAbove ?? "-"}</dd></div>
      </dl>
    </article>
  );
}

function compactDistribution(distribution) {
  if (!distribution) return null;
  return {
    count: distribution.count,
    p25: distribution.p25,
    p50: distribution.p50,
    p75: distribution.p75,
    p95: distribution.p95,
    max: distribution.max,
    avg: distribution.avg
  };
}

function compactInputProfileForExport(profile) {
  if (!profile) return null;
  return {
    inputKind: profile.inputKind,
    pointerType: profile.pointerType,
    confidence: profile.confidence,
    sampleCount: profile.sampleCount,
    sampleRateHz: profile.sampleRateHz,
    pressureSupported: profile.pressureSupported,
    avgContact: profile.averageContactMajor,
    maxContact: profile.maxContactMajor,
    raw: profile.rawPointerSupported,
    coalesced: profile.coalescedSupported
  };
}

function compactQualityForExport(quality) {
  if (!quality) return null;
  return {
    verdict: quality.verdict,
    strokes: quality.strokeCount,
    points: quality.pointCount,
    avgGap: quality.averagePointGap,
    maxGap: quality.largestPointGap,
    discontinuities: quality.discontinuities,
    acceptedSamples: quality.acceptedSamples
  };
}

function compactSampleSummaryForExport(summary) {
  if (!summary) return null;
  return {
    accepted: summary.acceptedCount,
    rejected: summary.rejectedCount,
    pointerTypes: summary.pointerTypes,
    rejectionReasons: summary.rejectionReasons,
    contactMajor: summary.contactMajor,
    pressure: summary.pressure,
    hz: summary.timing?.inferredHz,
    avgMs: summary.timing?.avgMs,
    raw: summary.rawEventCount,
    coalesced: summary.coalescedEventCount
  };
}

function compactPolicyForExport(policy) {
  if (!policy) return null;
  return {
    smoothing: policy.smoothing,
    minDistance: policy.minDistance,
    strokeWidth: policy.strokeWidth,
    palmMode: policy.palmMode,
    palmThreshold: policy.palmContactThreshold,
    hardPalmThreshold: policy.palmHardRejectContact,
    calibrationMode: policy.calibrationMode,
    maxGapPx: policy.maxGapPx
  };
}

function compactEventForExport(event) {
  return {
    event: event.eventType,
    source: event.source,
    pointer: event.pointerType,
    pressure: event.pressure,
    contact: event.contact,
    accepted: event.accepted,
    reason: event.reason
  };
}

function compactCalibrationCapture(capture) {
  if (!capture) return null;
  return {
    kind: capture.kind,
    palmMode: capture.palmMode,
    input: compactInputProfileForExport(capture.inputProfile),
    quality: compactQualityForExport(capture.quality),
    contact: compactDistribution(capture.contactDistribution),
    rejectedContact: compactDistribution(capture.rejectedContactDistribution),
    allContact: compactDistribution(capture.allContactDistribution),
    samples: compactSampleSummaryForExport(capture.sampleSummary)
  };
}

function useRafRedraw(draw) {
  const frameRef = useRef(null);
  return useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      draw();
    });
  }, [draw]);
}

export default function SlateInputLab() {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const strokesRef = useRef([]);
  const activeStrokeRef = useRef([]);
  const activePointerIdRef = useRef(null);
  const samplesRef = useRef([]);
  const rejectedSamplesRef = useRef([]);
  const eventLogRef = useRef([]);
  const statsTimerRef = useRef(null);
  const pointerSeenRef = useRef(false);
  const activeContactsRef = useRef(new Map());
  const rejectedPointerIdsRef = useRef(new Set());
  const [deviceProfile, setDeviceProfile] = useState(() => detectDeviceProfile());
  const [, setStatsVersion] = useState(0);
  const [showRaw, setShowRaw] = useState(true);
  const [exportJson, setExportJson] = useState("");
  const [manualFeedback, setManualFeedback] = useState({
    inputUsed: "",
    posture: "",
    feel: [],
    palmRested: false,
    note: ""
  });
  const [palmMode, setPalmMode] = useState("adaptive");
  const [calibratedPolicyEnabled, setCalibratedPolicyEnabled] = useState(false);
  const [calibrationCaptures, setCalibrationCaptures] = useState({
    writingOnly: null,
    palmOnly: null,
    palmRested: null
  });

  const inputProfile = classifyInputProfile(samplesRef.current, deviceProfile);
  const deviceProfileKey = deviceProfile.profileKey;
  const inputKind = inputProfile.inputKind;
  const calibrationResult = useMemo(
    () => assessPalmCalibration(calibrationCaptures, deviceProfile, inputProfile),
    [calibrationCaptures, deviceProfile, inputProfile]
  );
  const calibratedPalmOverrides = useMemo(() => {
    const generated = calibrationResult.generatedPolicy;
    if (!calibratedPolicyEnabled || !generated?.palmContactThreshold) return {};
    return {
      palmContactThreshold: generated.palmContactThreshold,
      palmHardRejectContact: generated.palmHardRejectContact,
      calibrationMode: generated.mode
    };
  }, [calibratedPolicyEnabled, calibrationResult]);
  const effectivePalmMode = calibratedPolicyEnabled && calibratedPalmOverrides.palmContactThreshold ? "adaptive" : palmMode;
  const policy = useMemo(
    () => ({ ...selectStrokePolicy({ profileKey: deviceProfileKey }, { inputKind }), ...calibratedPalmOverrides, palmMode: effectivePalmMode }),
    [calibratedPalmOverrides, deviceProfileKey, effectivePalmMode, inputKind]
  );
  const quality = diagnoseStrokeQuality(strokesRef.current, samplesRef.current, policy);
  const refreshStatsSoon = useCallback(() => {
    if (statsTimerRef.current) return;
    statsTimerRef.current = window.setTimeout(() => {
      statsTimerRef.current = null;
      setStatsVersion((version) => version + 1);
    }, 90);
  }, []);

  const captureCalibrationSample = useCallback((kind) => {
    const acceptedSamples = samplesRef.current.slice();
    const rejectedSamples = rejectedSamplesRef.current.slice();
    const allSamples = [...acceptedSamples, ...rejectedSamples];
    const capturedStrokes = activeStrokeRef.current.length
      ? [...strokesRef.current, activeStrokeRef.current]
      : strokesRef.current.slice();
    const capture = {
      kind,
      capturedAt: new Date().toISOString(),
      manualFeedback: { ...manualFeedback },
      palmMode,
      inputProfile: { ...inputProfile },
      quality: diagnoseStrokeQuality(capturedStrokes, acceptedSamples, policy),
      contactDistribution: summarizeContactDistribution(acceptedSamples),
      rejectedContactDistribution: summarizeContactDistribution(rejectedSamples),
      allContactDistribution: summarizeContactDistribution(allSamples),
      sampleSummary: summarizeSamples(acceptedSamples, rejectedSamples),
      strokeSummary: summarizeStrokes(capturedStrokes)
    };
    setCalibrationCaptures((current) => ({ ...current, [kind]: capture }));
    if (kind !== "palmRested") setCalibratedPolicyEnabled(false);
    setExportJson("");
  }, [inputProfile, manualFeedback, palmMode, policy]);

  const clearCalibration = useCallback(() => {
    setCalibrationCaptures({ writingOnly: null, palmOnly: null, palmRested: null });
    setCalibratedPolicyEnabled(false);
    setExportJson("");
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    const allStrokes = [...strokesRef.current];
    if (activeStrokeRef.current.length) allStrokes.push(activeStrokeRef.current);
    allStrokes.forEach((stroke) => {
      if (showRaw) drawStroke(ctx, stroke, policy, { raw: true });
      drawStroke(ctx, smoothStroke(stroke, policy), policy);
    });
  }, [policy, showRaw]);

  const scheduleRedraw = useRafRedraw(redraw);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    const ctx = canvas.getContext("2d", { alpha: true, desynchronized: true });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctxRef.current = ctx;
    redraw();
  }, [redraw]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("orientationchange", resizeCanvas);
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("orientationchange", resizeCanvas);
    };
  }, [resizeCanvas]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  const pushEventLog = useCallback((sample, accepted, reason = "accepted") => {
    eventLogRef.current = [{
      at: new Date().toLocaleTimeString(),
      eventType: sample.eventType,
      source: sample.source,
      pointerType: sample.pointerType,
      pressure: round(sample.pressure, 3),
      contact: round(sample.contactMajor, 1),
      accepted,
      reason
    }, ...eventLogRef.current].slice(0, 24);
  }, []);

  const recordSample = useCallback((sample, accepted, reason) => {
    const stampedSample = accepted ? sample : { ...sample, rejectionReason: reason };
    if (accepted) samplesRef.current = [...samplesRef.current, stampedSample].slice(-900);
    else rejectedSamplesRef.current = [...rejectedSamplesRef.current, stampedSample].slice(-350);
    pushEventLog(stampedSample, accepted, reason);
    refreshStatsSoon();
  }, [pushEventLog, refreshStatsSoon]);

  const updateActiveContact = useCallback((sample) => {
    if (sample.pointerType !== "touch") return;
    activeContactsRef.current.set(sample.pointerId, sample);
  }, []);

  const clearActiveContact = useCallback((pointerId) => {
    activeContactsRef.current.delete(pointerId);
    rejectedPointerIdsRef.current.delete(pointerId);
  }, []);

  const countNonRejectedTouchContacts = useCallback(() => {
    let count = 0;
    activeContactsRef.current.forEach((contact, pointerId) => {
      if (contact.pointerType === "touch" && !rejectedPointerIdsRef.current.has(pointerId)) count += 1;
    });
    return count;
  }, []);

  const replaceActiveTouchIfBetterWriter = useCallback((sample) => {
    if (policy.palmMode === "off" || sample.pointerType !== "touch") return false;
    const activePointerId = activePointerIdRef.current;
    if (activePointerId === null || activePointerId === sample.pointerId) return false;
    const threshold = policy.palmContactThreshold || DEFAULT_POLICY.palmContactThreshold;
    const activeContact = activeContactsRef.current.get(activePointerId);
    const activeStroke = activeStrokeRef.current;
    const firstPoint = activeStroke[0];
    const lastPoint = activeStroke[activeStroke.length - 1];
    const activeTravel = firstPoint && lastPoint ? distance(firstPoint, lastPoint) : 0;
    const activeContactMajor = Math.max(activeContact?.contactMajor || 0, firstPoint?.contactMajor || 0);
    const activeIsUncommitted = activeTravel < 4 && activeStroke.length <= 8;
    const activeLooksPalm = activeContactMajor >= threshold || activeIsUncommitted;
    const sampleLooksWriter = sample.contactMajor > 0 && sample.contactMajor < threshold * 0.95;
    if (!activeLooksPalm || !sampleLooksWriter) return false;
    rejectedPointerIdsRef.current.add(activePointerId);
    activeStrokeRef.current = [];
    activePointerIdRef.current = null;
    return true;
  }, [policy.palmContactThreshold, policy.palmMode]);

  const classifyPalmCandidate = useCallback((sample) => {
    if (policy.palmMode === "off" || sample.pointerType !== "touch") return "";
    const activePointerId = activePointerIdRef.current;
    const activeTouchCount = countNonRejectedTouchContacts();
    const contact = sample.contactMajor || 0;
    const threshold = policy.palmContactThreshold || DEFAULT_POLICY.palmContactThreshold;
    const hardThreshold = policy.palmHardRejectContact || DEFAULT_POLICY.palmHardRejectContact;
    const secondaryThreshold = threshold * (policy.palmSecondaryContactRatio || DEFAULT_POLICY.palmSecondaryContactRatio);
    const pressureCeiling = policy.palmPressureCeiling ?? DEFAULT_POLICY.palmPressureCeiling;
    const lowPressure = !sample.pressure || sample.pressure <= pressureCeiling;

    if (rejectedPointerIdsRef.current.has(sample.pointerId)) return "known rejected palm pointer";
    if (activePointerId !== null && sample.pointerId !== activePointerId) return "secondary contact while drawing";
    if (contact >= hardThreshold) return "hard palm contact threshold";
    if (policy.palmMode === "strict" && contact >= threshold) return "strict palm contact threshold";
    if (contact >= threshold * 1.15 && lowPressure) return "broad single touch contact";
    if (activeTouchCount > 1 && contact >= threshold && lowPressure) return "large multi-touch contact";
    if (!sample.isPrimary && activeTouchCount > 1 && contact >= secondaryThreshold) return "non-primary broad contact";
    return "";
  }, [countNonRejectedTouchContacts, policy.palmContactThreshold, policy.palmHardRejectContact, policy.palmMode, policy.palmPressureCeiling, policy.palmSecondaryContactRatio]);

  const shouldRejectSample = useCallback((sample) => {
    if (activePointerIdRef.current !== null && sample.pointerId !== activePointerIdRef.current) {
      if (replaceActiveTouchIfBetterWriter(sample)) return "";
      return "secondary pointer while drawing";
    }
    return classifyPalmCandidate(sample);
  }, [classifyPalmCandidate, replaceActiveTouchIfBetterWriter]);

  const appendPoint = useCallback((sample) => {
    const stroke = activeStrokeRef.current;
    const previous = stroke[stroke.length - 1];
    if (previous && distance(previous, sample) < Math.max(0.1, policy.minDistance * 0.45)) return;
    activeStrokeRef.current = [...stroke, sample];
    scheduleRedraw();
  }, [policy.minDistance, scheduleRedraw]);

  const beginStroke = useCallback((sample) => {
    updateActiveContact(sample);
    const reason = shouldRejectSample(sample);
    if (reason) {
      if (sample.pointerType === "touch") rejectedPointerIdsRef.current.add(sample.pointerId);
      recordSample(sample, false, reason);
      return false;
    }
    activePointerIdRef.current = sample.pointerId;
    activeStrokeRef.current = [sample];
    recordSample(sample, true, "stroke start");
    scheduleRedraw();
    return true;
  }, [recordSample, scheduleRedraw, shouldRejectSample, updateActiveContact]);

  const moveStroke = useCallback((sample) => {
    updateActiveContact(sample);
    const reason = shouldRejectSample(sample);
    if (reason) {
      if (sample.pointerType === "touch") rejectedPointerIdsRef.current.add(sample.pointerId);
      if (sample.pointerType === "touch" && sample.pointerId === activePointerIdRef.current && reason !== "secondary pointer while drawing") {
        activeStrokeRef.current = [];
        activePointerIdRef.current = null;
        scheduleRedraw();
      }
      recordSample(sample, false, reason);
      return;
    }
    if (activePointerIdRef.current === null) return;
    recordSample(sample, true, "move");
    appendPoint(sample);
  }, [appendPoint, recordSample, scheduleRedraw, shouldRejectSample, updateActiveContact]);

  const endStroke = useCallback((sample) => {
    const isActivePointer = activePointerIdRef.current !== null && sample.pointerId === activePointerIdRef.current;
    if (!isActivePointer) {
      if (rejectedPointerIdsRef.current.has(sample.pointerId)) recordSample(sample, false, "rejected pointer ended");
      clearActiveContact(sample.pointerId);
      return;
    }
    recordSample(sample, true, "stroke end");
    appendPoint(sample);
    if (activeStrokeRef.current.length > 1) strokesRef.current = [...strokesRef.current, activeStrokeRef.current];
    activeStrokeRef.current = [];
    activePointerIdRef.current = null;
    clearActiveContact(sample.pointerId);
    refreshStatsSoon();
    scheduleRedraw();
  }, [appendPoint, clearActiveContact, recordSample, refreshStatsSoon, scheduleRedraw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const opts = { passive: false };
    const down = (event) => {
      pointerSeenRef.current = true;
      event.preventDefault();
      const accepted = beginStroke(normalizePointerEvent(event, canvas));
      if (accepted) canvas.setPointerCapture?.(event.pointerId);
    };
    const move = (event) => {
      pointerSeenRef.current = true;
      event.preventDefault();
      collectPointerSamples(event, canvas, policy).forEach(moveStroke);
    };
    const raw = (event) => {
      pointerSeenRef.current = true;
      if (!policy.rawEvents) return;
      event.preventDefault();
      moveStroke(normalizePointerEvent(event, canvas, "raw"));
    };
    const up = (event) => {
      pointerSeenRef.current = true;
      event.preventDefault();
      endStroke(normalizePointerEvent(event, canvas));
      if (canvas.hasPointerCapture?.(event.pointerId)) canvas.releasePointerCapture?.(event.pointerId);
    };
    const touchStart = (event) => {
      if (pointerSeenRef.current) return;
      event.preventDefault();
      const touch = event.changedTouches[0];
      if (touch) beginStroke(normalizeTouch(touch, event, canvas));
    };
    const touchMove = (event) => {
      if (pointerSeenRef.current) return;
      event.preventDefault();
      Array.from(event.changedTouches).forEach((touch) => moveStroke(normalizeTouch(touch, event, canvas)));
    };
    const touchEnd = (event) => {
      if (pointerSeenRef.current) return;
      event.preventDefault();
      Array.from(event.changedTouches).forEach((touch) => endStroke(normalizeTouch(touch, event, canvas)));
    };
    canvas.addEventListener("pointerdown", down, opts);
    canvas.addEventListener("pointermove", move, opts);
    canvas.addEventListener("pointerrawupdate", raw, opts);
    canvas.addEventListener("pointerup", up, opts);
    canvas.addEventListener("pointercancel", up, opts);
    canvas.addEventListener("touchstart", touchStart, opts);
    canvas.addEventListener("touchmove", touchMove, opts);
    canvas.addEventListener("touchend", touchEnd, opts);
    canvas.addEventListener("touchcancel", touchEnd, opts);
    return () => {
      canvas.removeEventListener("pointerdown", down);
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerrawupdate", raw);
      canvas.removeEventListener("pointerup", up);
      canvas.removeEventListener("pointercancel", up);
      canvas.removeEventListener("touchstart", touchStart);
      canvas.removeEventListener("touchmove", touchMove);
      canvas.removeEventListener("touchend", touchEnd);
      canvas.removeEventListener("touchcancel", touchEnd);
    };
  }, [beginStroke, endStroke, moveStroke, policy]);
  const clearCanvas = useCallback(() => {
    strokesRef.current = [];
    activeStrokeRef.current = [];
    activePointerIdRef.current = null;
    samplesRef.current = [];
    rejectedSamplesRef.current = [];
    eventLogRef.current = [];
    activeContactsRef.current = new Map();
    rejectedPointerIdsRef.current = new Set();
    setExportJson("");
    setStatsVersion((version) => version + 1);
    scheduleRedraw();
  }, [scheduleRedraw]);

  const resetProfiles = useCallback(() => {
    setDeviceProfile(detectDeviceProfile());
    samplesRef.current = [];
    rejectedSamplesRef.current = [];
    eventLogRef.current = [];
    activeContactsRef.current = new Map();
    rejectedPointerIdsRef.current = new Set();
    pointerSeenRef.current = false;
    setCalibrationCaptures({ writingOnly: null, palmOnly: null, palmRested: null });
    setCalibratedPolicyEnabled(false);
    setExportJson("");
    setStatsVersion((version) => version + 1);
  }, []);

  const exportReport = useCallback(async () => {
    const report = {
      schemaVersion: "slate-input-lab-minimal-v1",
      generatedAt: new Date().toISOString(),
      feedback: manualFeedback,
      device: {
        profileKey: deviceProfile.profileKey,
        formFactor: deviceProfile.formFactor,
        os: deviceProfile.os,
        browser: deviceProfile.browser,
        touchPoints: deviceProfile.maxTouchPoints,
        anyCoarse: deviceProfile.anyCoarsePointer,
        anyFine: deviceProfile.anyFinePointer,
        dpr: deviceProfile.devicePixelRatio,
        viewport: deviceProfile.viewport
      },
      input: compactInputProfileForExport(inputProfile),
      policy: compactPolicyForExport(policy),
      palm: {
        selectedMode: palmMode,
        effectiveMode: effectivePalmMode,
        activeTouchContacts: activeContactsRef.current.size,
        rejectedPointerIds: rejectedPointerIdsRef.current.size
      },
      calibration: {
        enabled: calibratedPolicyEnabled,
        overrides: calibratedPalmOverrides,
        result: calibrationResult,
        captures: {
          writingOnly: compactCalibrationCapture(calibrationCaptures.writingOnly),
          palmOnly: compactCalibrationCapture(calibrationCaptures.palmOnly),
          palmRested: compactCalibrationCapture(calibrationCaptures.palmRested)
        }
      },
      quality: compactQualityForExport(quality),
      samples: compactSampleSummaryForExport(summarizeSamples(samplesRef.current, rejectedSamplesRef.current)),
      acceptedContact: compactDistribution(summarizeContactDistribution(samplesRef.current)),
      rejectedContact: compactDistribution(summarizeContactDistribution(rejectedSamplesRef.current)),
      recentEvents: eventLogRef.current.slice(0, 8).map(compactEventForExport)
    };
    const json = JSON.stringify(report, null, 2);
    setExportJson(json);
    try {
      await navigator.clipboard?.writeText(json);
    } catch (error) {
      // The textarea remains the fallback when clipboard permission is denied.
    }
  }, [calibratedPalmOverrides, calibratedPolicyEnabled, calibrationCaptures, calibrationResult, deviceProfile, effectivePalmMode, inputProfile, manualFeedback, palmMode, policy, quality]);

  const deviceItems = [
    ["profileKey", deviceProfile.profileKey],
    ["formFactor", deviceProfile.formFactor],
    ["os", deviceProfile.os],
    ["browser", deviceProfile.browser],
    ["touchPoints", deviceProfile.maxTouchPoints],
    ["any coarse/fine", `${deviceProfile.anyCoarsePointer}/${deviceProfile.anyFinePointer}`],
    ["dpr", deviceProfile.devicePixelRatio],
    ["viewport", `${deviceProfile.viewport.width} x ${deviceProfile.viewport.height}`]
  ];
  const inputItems = [
    ["inputKind", inputProfile.inputKind],
    ["pointerType", inputProfile.pointerType],
    ["confidence", inputProfile.confidence],
    ["samples", inputProfile.sampleCount],
    ["sample rate", `${inputProfile.sampleRateHz} Hz`],
    ["pressure", inputProfile.pressureSupported],
    ["avg contact", inputProfile.averageContactMajor],
    ["raw/coalesced", `${inputProfile.rawPointerSupported}/${inputProfile.coalescedSupported}`]
  ];
  const policyItems = [
    ["smoothing", policy.smoothing],
    ["minDistance", policy.minDistance],
    ["strokeWidth", policy.strokeWidth],
    ["pressure", policy.pressure],
    ["rawEvents", policy.rawEvents],
    ["coalesced", policy.coalesced],
    ["palm mode", palmMode + (effectivePalmMode !== palmMode ? " -> " + effectivePalmMode : "")],
    ["palm threshold", policy.palmContactThreshold],
    ["hard palm threshold", policy.palmHardRejectContact],
    ["calibration", policy.calibrationMode || "default"],
    ["max gap", policy.maxGapPx]
  ];
  const qualityItems = [
    ["verdict", quality.verdict],
    ["strokes", quality.strokeCount],
    ["points", quality.pointCount],
    ["avg gap", quality.averagePointGap],
    ["largest gap", quality.largestPointGap],
    ["discontinuities", quality.discontinuities],
    ["accepted samples", quality.acceptedSamples],
    ["rejected samples", rejectedSamplesRef.current.length]
  ];

  return (
    <div className="slate-lab-page">
      <section className="slate-lab-hero">
        <div>
          <p className="slate-lab-kicker">Experimental slate infrastructure</p>
          <h1>Slate Input Lab</h1>
          <p>
            Identify the device profile at load, infer input type from the first strokes, and test a merged rendering policy.
            Nothing here changes the production practice slate.
          </p>
        </div>
        <div className="slate-lab-hero-actions">
          <button className="secondary-button" type="button" onClick={resetProfiles}>Reset profile</button>
          <Link className="secondary-button" to="/dashboard">Back to dashboard</Link>
        </div>
      </section>

      <div className="slate-lab-panel-grid">
        <DataCard title="Device" eyebrow="detected at page load" items={deviceItems} />
        <DataCard title="Input" eyebrow="learned from first ink" items={inputItems} />
        <DataCard title="Policy" eyebrow="device + input merge" items={policyItems} />
        <DataCard title="Quality" eyebrow="stroke diagnostics" items={qualityItems} />
      </div>

      <section className="slate-lab-calibration-card">
        <div className="slate-lab-calibration-header">
          <div>
            <div className="slate-lab-card-eyebrow">device-specific palm model</div>
            <h2>Palm Calibration</h2>
            <p>Use Palm = off only for the first two captures: stylus-only, then palm-only. After the recommendation appears, turn it on, clear the canvas, and capture validation by writing with palm rested.</p>
          </div>
          <div className="slate-lab-calibration-actions">
            <button className="secondary-button" type="button" onClick={() => captureCalibrationSample("writingOnly")}>Capture stylus-only</button>
            <button className="secondary-button" type="button" onClick={() => captureCalibrationSample("palmOnly")}>Capture palm-only</button>
            <button className="secondary-button" type="button" disabled={!calibratedPolicyEnabled} onClick={() => captureCalibrationSample("palmRested")}>Capture validation</button>
            <button
              className="secondary-button"
              type="button"
              disabled={!calibrationResult.generatedPolicy?.palmContactThreshold}
              onClick={() => {
                if (!calibratedPolicyEnabled) setPalmMode("adaptive");
                setCalibratedPolicyEnabled((enabled) => !enabled);
              }}
            >
              {calibratedPolicyEnabled ? "Using recommendation" : "Use recommendation in lab"}
            </button>
            <button className="secondary-button" type="button" onClick={clearCalibration}>Clear calibration</button>
          </div>
        </div>
        <div className="slate-lab-calibration-grid">
          <CalibrationCaptureCard title="1. Stylus Only" capture={calibrationCaptures.writingOnly} />
          <CalibrationCaptureCard title="2. Palm Only" capture={calibrationCaptures.palmOnly} />
          <CalibrationCaptureCard title="3. Validation: Palm Rested" capture={calibrationCaptures.palmRested} />
          <CalibrationRecommendationCard result={calibrationResult} />
        </div>
      </section>

      <section className="slate-lab-canvas-card">
        <div className="slate-lab-toolbar">
          <div>
            <h2>Write test</h2>
            <p>Try the same expression with finger, disc stylus, active stylus, and mouse. Compare gaps, sample rate, and policy.</p>
          </div>
          <div className="slate-lab-actions">
            <label className="slate-lab-toggle">
              Palm
              <select value={palmMode} onChange={(event) => setPalmMode(event.target.value)}>
                <option value="adaptive">adaptive</option>
                <option value="strict">strict</option>
                <option value="off">off</option>
              </select>
            </label>
            <label className="slate-lab-toggle">
              <input type="checkbox" checked={showRaw} onChange={(event) => setShowRaw(event.target.checked)} />
              show raw path
            </label>
            <button className="secondary-button" type="button" onClick={clearCanvas}>Clear</button>
            <button className="primary-button" type="button" onClick={exportReport}>Export compact report</button>
          </div>
        </div>
        <div className="slate-lab-feedback-strip">
          <label>Input used
            <select value={manualFeedback.inputUsed} onChange={(event) => setManualFeedback((current) => ({ ...current, inputUsed: event.target.value }))}>
              <option value="">Choose</option>
              <option value="finger">Finger</option>
              <option value="capacitive-disc">Capacitive disc</option>
              <option value="rubber-tip">Rubber tip</option>
              <option value="active-stylus">Active stylus</option>
              <option value="mouse">Mouse</option>
            </select>
          </label>
          <label>Device posture
            <select value={manualFeedback.posture} onChange={(event) => setManualFeedback((current) => ({ ...current, posture: event.target.value }))}>
              <option value="">Choose</option>
              <option value="flat-table">Flat on table</option>
              <option value="held-reading">Held like reading</option>
              <option value="laptop-mode">Laptop mode</option>
              <option value="tent-mode">Tent mode</option>
            </select>
          </label>
          <label className="slate-lab-toggle slate-lab-palm-rested">
            <input
              type="checkbox"
              checked={manualFeedback.palmRested}
              onChange={(event) => setManualFeedback((current) => ({ ...current, palmRested: event.target.checked }))}
            />
            Palm rested while writing
          </label>
          <fieldset className="slate-lab-feel-options">
            <legend>Felt quality</legend>
            {["smooth", "laggy", "broken", "offset", "not-detected"].map((value) => (
              <label key={value}>
                <input
                  type="checkbox"
                  checked={manualFeedback.feel.includes(value)}
                  onChange={(event) => setManualFeedback((current) => ({
                    ...current,
                    feel: event.target.checked
                      ? [...current.feel, value]
                      : current.feel.filter((item) => item !== value)
                  }))}
                />
                {value.replace(/-/g, " ")}
              </label>
            ))}
          </fieldset>
          <label className="slate-lab-feedback-note">Observation
            <input value={manualFeedback.note} onChange={(event) => setManualFeedback((current) => ({ ...current, note: event.target.value }))} placeholder="Example: smooth only when slow, breaks on curves" />
          </label>
        </div>
        <div className="slate-lab-canvas-wrap">
          <canvas ref={canvasRef} className="slate-lab-canvas" aria-label="Slate input testing canvas" />
        </div>
      </section>

      <section className="slate-lab-bottom-grid">
        <div className="slate-lab-log-card">
          <div className="slate-lab-section-heading">
            <h2>Recent input events</h2>
            <span>{eventLogRef.current.length} shown</span>
          </div>
          <div className="slate-lab-table-wrap">
            <table className="slate-lab-table">
              <thead>
                <tr><th>time</th><th>event</th><th>source</th><th>type</th><th>pressure</th><th>contact</th><th>status</th></tr>
              </thead>
              <tbody>
                {eventLogRef.current.length ? eventLogRef.current.map((entry, index) => (
                  <tr key={`${entry.at}-${entry.eventType}-${index}`} className={entry.accepted ? "" : "is-rejected"}>
                    <td>{entry.at}</td><td>{entry.eventType}</td><td>{entry.source}</td><td>{entry.pointerType}</td>
                    <td>{entry.pressure}</td><td>{entry.contact}</td><td>{entry.accepted ? "accepted" : entry.reason}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="7">Start writing on the canvas to capture events.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="slate-lab-log-card">
          <div className="slate-lab-section-heading">
            <h2>Compact profile JSON</h2>
            <span>paste this here</span>
          </div>
          <textarea
            className="slate-lab-report"
            value={exportJson || "Click Export compact report after one device + stylus test. This version omits raw point dumps."}
            readOnly
          />
        </div>
      </section>
    </div>
  );
}
